/**
 * HeadlessConfig Accessor Class
 * Provides typed access to raw headless metafield config
 *
 * Usage:
 *   const config = new HeadlessConfig(rawMetafield);
 *   config.bool('ga4_status');          // true/false
 *   config.str('ga4_id');               // "G-XXXXXXX"
 *   config.str('ga4_id', true);         // secondary: "G-YYYYYYY"
 *   config.int('chunk_size');           // 10
 */

import type { HeadlessMetafield, MetafieldValue } from '../types/headless-config';
import { CONFIG_NAMESPACES, EVENT_ABBREVIATIONS } from '../types/headless-config';

export class HeadlessConfig {
  constructor(private readonly raw: HeadlessMetafield) {}

  /**
   * Get raw metafield value by key
   */
  private get(key: string, secondary = false): MetafieldValue | undefined {
    const namespace = secondary ? CONFIG_NAMESPACES.SECONDARY : CONFIG_NAMESPACES.PRIMARY;
    return this.raw[`${namespace}.${key}`];
  }

  /**
   * Check if a key exists in the config
   */
  has(key: string, secondary = false): boolean {
    return this.get(key, secondary) !== undefined;
  }

  /**
   * Get boolean value
   * Returns false if key doesn't exist or value is not 'true'
   */
  bool(key: string, secondary = false): boolean {
    const field = this.get(key, secondary);
    if (!field) return false;
    return field.value === 'true';
  }

  /**
   * Get string value
   * Returns empty string if key doesn't exist
   */
  str(key: string, secondary = false): string {
    const field = this.get(key, secondary);
    if (!field) return '';
    return field.value;
  }

  /**
   * Get integer value
   * Returns 0 if key doesn't exist or value is not a valid number
   */
  int(key: string, secondary = false): number {
    const field = this.get(key, secondary);
    if (!field) return 0;
    const parsed = parseInt(field.value, 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Get events config object for a platform
   * Reads keys like: {platform}_events_{eventAbbr}
   *
   * @example
   * config.events('ga4')
   * // Returns: { add_to_cart: true, view_item: true, ... }
   */
  events(platformPrefix: string, secondary = false): Record<string, boolean> {
    const result: Record<string, boolean> = {};

    for (const [abbr, fullName] of Object.entries(EVENT_ABBREVIATIONS)) {
      const key = `${platformPrefix}_events_${abbr}`;
      if (this.has(key, secondary)) {
        result[fullName] = this.bool(key, secondary);
      }
    }

    return result;
  }

  /**
   * Get Google Ads conversion config
   * Reads keys like: gads_conversions_{conversionType}_{status|label}
   */
  gadsConversions(secondary = false): Record<string, { status: boolean; value: string }> {
    const result: Record<string, { status: boolean; value: string }> = {};
    const conversionTypes = ['atc', 'bc', 'purchase'];

    for (const type of conversionTypes) {
      const statusKey = `gads_conversions_${type}_status`;
      const labelKey = `gads_conversions_${type}_label`;

      if (this.has(statusKey, secondary)) {
        const fullName = EVENT_ABBREVIATIONS[type as keyof typeof EVENT_ABBREVIATIONS] || type;
        result[fullName] = {
          status: this.bool(statusKey, secondary),
          value: this.str(labelKey, secondary),
        };
      }
    }

    return result;
  }

  /**
   * Check if platform is enabled
   * Checks both status and ID presence
   */
  isPlatformEnabled(platformPrefix: string, secondary = false): boolean {
    const statusKey = `${platformPrefix}_status`;
    const idKey = `${platformPrefix}_id`;

    // Special cases for platform naming
    const actualIdKey =
      platformPrefix === 'server_side' ? 'server_side_store_id' : idKey;

    return this.bool(statusKey, secondary) && !!this.str(actualIdKey, secondary);
  }

  /**
   * Get platform ID
   */
  platformId(platformPrefix: string, secondary = false): string {
    const idKey =
      platformPrefix === 'server_side'
        ? 'server_side_store_id'
        : `${platformPrefix}_id`;
    return this.str(idKey, secondary);
  }

  /**
   * Get all keys in the config (for debugging)
   */
  keys(): string[] {
    return Object.keys(this.raw);
  }

  /**
   * Get raw metafield object (for advanced usage)
   */
  getRaw(): HeadlessMetafield {
    return this.raw;
  }
}

/**
 * Create HeadlessConfig from raw metafield JSON
 */
export function createHeadlessConfig(raw: HeadlessMetafield): HeadlessConfig {
  return new HeadlessConfig(raw);
}
